'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { CheckCircle, MapPin, Phone, Pickaxe } from 'lucide-react'

import type { HomePageContent } from '@/lib/content/defaults/home'
import { HalloweenTrinket } from '@/components/halloween'

const STEP_POOLS = ['friendly-hands', 'lanterns', 'excavation', 'landscaping'] as const
const STEP_ICONS = [Phone, MapPin, Pickaxe, CheckCircle] as const

interface ProcessTimelineProps {
  steps: HomePageContent['process']['steps']
  stepLabel: string
  onProgress?: (progress: number) => void
}

export function ProcessTimeline({ steps, stepLabel, onProgress }: ProcessTimelineProps) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const [progress, setProgress] = useState(0)
  const onProgressRef = useRef(onProgress)

  useEffect(() => {
    onProgressRef.current = onProgress
  }, [onProgress])

  const rafRef = useRef(0)

  const handleScroll = useCallback(() => {
    // Gate with rAF to avoid layout thrashing from getBoundingClientRect
    if (rafRef.current) return
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0
      const el = sectionRef.current
      if (!el) return

      const rect = el.getBoundingClientRect()
      const viewH = window.innerHeight
      // On mobile, use a larger denominator so progress starts earlier
      const span = viewH < 768 ? viewH * 0.85 : viewH * 0.7
      const raw = (viewH - rect.top) / span
      const clamped = Math.max(0, Math.min(1, raw))
      setProgress(clamped)
      onProgressRef.current?.(clamped)
    })
  }, [])

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return

    let frame1 = 0

    const attach = () => {
      window.addEventListener('scroll', handleScroll, { passive: true })
      window.addEventListener('resize', handleScroll, { passive: true })
      // Measure after two frames to ensure layout is settled on CSR navigation
      frame1 = requestAnimationFrame(() => {
        frame1 = requestAnimationFrame(() => {
          rafRef.current = 0
          handleScroll()
        })
      })
    }

    const detach = () => {
      window.removeEventListener('scroll', handleScroll)
      window.removeEventListener('resize', handleScroll)
      cancelAnimationFrame(frame1)
      cancelAnimationFrame(rafRef.current)
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) attach()
        else detach()
      },
      { rootMargin: '200px' }
    )
    observer.observe(el)

    return () => {
      observer.disconnect()
      detach()
    }
  }, [handleScroll])

  // Each node activates at 0.15, 0.35, 0.55, 0.75
  const nodeThresholds = [0.15, 0.35, 0.55, 0.75]
  // Scale line so it reaches 100% when the last node activates
  const lineProgress = Math.min(1, progress / 0.75)

  return (
    <div ref={sectionRef}>
      {/* Desktop: horizontal */}
      <div className="hidden md:block">
        <div className="relative mx-auto max-w-5xl px-12 pt-6">
          {/* SVG line with rough filter, behind nodes */}
          <div className="absolute top-[4.5rem] right-12 left-12 -translate-y-1/2">
            <svg
              viewBox="0 0 1000 14"
              preserveAspectRatio="none"
              className="h-3.5 w-full overflow-visible"
              aria-hidden="true"
            >
              <defs>
                <filter id="rough-line" x="-5%" y="-50%" width="110%" height="200%">
                  <feTurbulence
                    type="turbulence"
                    baseFrequency="0.04 0.08"
                    numOctaves={3}
                    seed={17}
                    stitchTiles="stitch"
                    result="noise"
                  />
                  <feDisplacementMap
                    in="SourceGraphic"
                    in2="noise"
                    scale={4}
                    xChannelSelector="R"
                    yChannelSelector="G"
                  />
                </filter>
                <linearGradient id="line-grad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#2F9E34" />
                  <stop offset="50%" stopColor="#852E85" />
                  <stop offset="100%" stopColor="#99F66E" />
                </linearGradient>
              </defs>
              {/* Track */}
              <rect
                x="0"
                y="5"
                width="1000"
                height="4"
                rx="2"
                fill="rgba(255,255,255,0.08)"
                filter="url(#rough-line)"
              />
              {/* Progress fill */}
              <rect
                x="0"
                y="5"
                width={1000 * lineProgress}
                height="4"
                rx="2"
                fill="url(#line-grad)"
                filter="url(#rough-line)"
                style={{ transition: 'width 0.1s linear' }}
              />
            </svg>
          </div>

          {/* Nodes */}
          <div className="relative grid grid-cols-4 gap-8">
            {steps.map((item, i) => {
              const active = progress >= nodeThresholds[i]
              const StepIcon = STEP_ICONS[i]
              return (
                <div key={i} className="flex flex-col items-center text-center">
                  <div className="relative z-20 flex size-24 items-center justify-center">
                    <HalloweenTrinket
                      pool={STEP_POOLS[i]}
                      initial={i}
                      className="absolute -top-5 left-1/2 z-30 !size-11 -translate-x-1/2"
                    />
                    <div className="border-brand-chartreuse-vivid/50 relative z-10 flex size-16 items-center justify-center rounded-full border-2 bg-[#1a0e20] shadow-[0_0_18px_rgba(153,246,110,0.16)]">
                      <StepIcon className="text-brand-chartreuse-vivid size-7" aria-hidden="true" />
                      <span className="font-heading border-brand-lavender/60 absolute -right-2 -bottom-2 flex size-7 items-center justify-center rounded-full border bg-[#1a0e20] text-sm font-bold text-white">
                        {i + 1}
                      </span>
                    </div>
                  </div>

                  {/* Step label */}
                  <span
                    className="font-heading mt-4 text-base font-bold tracking-wide uppercase transition-colors duration-500"
                    style={{ color: active ? '#e995e8' : 'rgba(233,149,232,0.6)' }}
                  >
                    {stepLabel} {i + 1}
                  </span>
                  <h3
                    className="font-heading mt-1 text-lg font-bold transition-colors duration-500"
                    style={{ color: active ? '#ffffff' : 'rgba(255,255,255,0.85)' }}
                  >
                    {item.label}
                  </h3>
                  <p
                    className="mt-1 text-base transition-colors duration-500"
                    style={{ color: active ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.6)' }}
                  >
                    {item.detail}
                  </p>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Mobile/tablet: vertical */}
      <div className="md:hidden">
        <div className="mx-auto max-w-md px-4">
          {/* Nodes. The line is positioned inside this container so it spans
              from the first circle centre to the last circle centre. */}
          <div className="relative flex flex-col gap-12">
            {/* Vertical SVG line. Top and bottom inset by half the circle height
                so the line starts and ends at circle centres. */}
            <div className="absolute top-10 bottom-10 left-10 -translate-x-1/2">
              <svg
                viewBox="0 0 14 400"
                preserveAspectRatio="none"
                className="h-full w-3.5 overflow-visible"
                aria-hidden="true"
              >
                <defs>
                  <filter id="rough-line-v" x="-50%" y="-5%" width="200%" height="110%">
                    <feTurbulence
                      type="turbulence"
                      baseFrequency="0.08 0.04"
                      numOctaves={3}
                      seed={17}
                      stitchTiles="stitch"
                      result="noise"
                    />
                    <feDisplacementMap
                      in="SourceGraphic"
                      in2="noise"
                      scale={4}
                      xChannelSelector="R"
                      yChannelSelector="G"
                    />
                  </filter>
                  <linearGradient id="line-grad-v" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2F9E34" />
                    <stop offset="50%" stopColor="#852E85" />
                    <stop offset="100%" stopColor="#99F66E" />
                  </linearGradient>
                </defs>
                {/* Track */}
                <rect
                  x="5"
                  y="0"
                  width="4"
                  height="400"
                  rx="2"
                  fill="rgba(255,255,255,0.08)"
                  filter="url(#rough-line-v)"
                />
                {/* Progress fill */}
                <rect
                  x="5"
                  y="0"
                  width="4"
                  height={400 * lineProgress}
                  rx="2"
                  fill="url(#line-grad-v)"
                  filter="url(#rough-line-v)"
                  style={{ transition: 'height 0.1s linear' }}
                />
              </svg>
            </div>
            {steps.map((item, i) => {
              const active = progress >= nodeThresholds[i]
              const StepIcon = STEP_ICONS[i]
              return (
                <div key={i} className="flex items-start gap-5">
                  <div className="relative z-20 flex size-20 shrink-0 items-center justify-center">
                    <HalloweenTrinket
                      pool={STEP_POOLS[i]}
                      initial={i}
                      className="absolute -top-4 left-1/2 z-30 !size-9 -translate-x-1/2"
                    />
                    <div className="border-brand-chartreuse-vivid/50 relative z-10 flex size-14 items-center justify-center rounded-full border-2 bg-[#1a0e20] shadow-[0_0_18px_rgba(153,246,110,0.16)]">
                      <StepIcon className="text-brand-chartreuse-vivid size-6" aria-hidden="true" />
                      <span className="font-heading border-brand-lavender/60 absolute -right-2 -bottom-2 flex size-6 items-center justify-center rounded-full border bg-[#1a0e20] text-xs font-bold text-white">
                        {i + 1}
                      </span>
                    </div>
                  </div>

                  {/* Text */}
                  <div className="pt-1">
                    <span
                      className="font-heading text-base font-bold tracking-wide uppercase transition-colors duration-500"
                      style={{ color: active ? '#e995e8' : 'rgba(233,149,232,0.6)' }}
                    >
                      {stepLabel} {i + 1}
                    </span>
                    <h3
                      className="font-heading text-lg font-bold transition-colors duration-500"
                      style={{ color: active ? '#ffffff' : 'rgba(255,255,255,0.85)' }}
                    >
                      {item.label}
                    </h3>
                    <p
                      className="mt-0.5 text-base transition-colors duration-500"
                      style={{
                        color: active ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.6)',
                      }}
                    >
                      {item.detail}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
